import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { API_BASE } from '../client/config';
import { describeBackupError } from '@cornerstone/shared';
import { Button } from './ui';
import { ContentSkeleton } from './skeleton/ContentSkeleton';
import { BoPage, Section, formatBytes, formatDateTime } from '../pages/bo/ui';
import { dateGroupSummary, defaultOpenDates, groupBackupSetsByDate } from './backupSetGroups';
import { restorePlan } from './backupRestoreCommand';

type BackupKind = 'db' | 'media';
type BackupErrorReport = {
  operation: 'run' | 'remote' | 'retention' | 'verify' | 'restore';
  code: string; message: string; createdAt: string; setId: string | null;
  /** 어느 종류의 백업에서 났는가 — 같은 종류가 성공하면 서버가 이 오류를 내린다. */
  kind?: BackupKind;
};
type Verification = {
  id: string; ok: boolean; expectedFiles: number; files: number; bytes: number;
  integrity: 'ok' | 'failed' | 'not-checked'; errors: BackupErrorReport[];
};
type BackupSet = {
  id: string; kind: BackupKind; status: 'complete'; createdAt: string;
  fileCount: number; totalBytes: number;
  remote: { status: 'not-configured' | 'pending' | 'success' | 'error'; checkedAt: string | null; error: BackupErrorReport | null };
};
type BackupJob = {
  id: string; kind: BackupKind | 'verify' | 'check'; status: 'running' | 'success' | 'partial' | 'failed'; startedAt: string;
  finishedAt?: string; setId?: string; error?: string; verification?: Verification;
};
type BackupOverview = {
  busy: boolean; sets: BackupSet[]; latestError: BackupErrorReport | null;
  automatic: boolean; intervals: { db: number; media: number }; lastJob: BackupJob | null;
  latestVerification: Verification | null;
  coordinatorError: string | null;
  configuration: { directory: string; bucket: string | null; prefix: string | null; region: string; error: string | null };
};
type BackupAction = { kind: BackupKind } | { kind: 'verify' | 'restore'; setId: string };
const KIND_LABELS = { db: 'DB 백업', media: 'DB·참조 미디어 백업', verify: '격리 복구 검증', restore: '이 시점으로 복구', check: '백업 파일 전체 검사' };
const JOB_LABELS = { running: '실행 중', success: '완료', partial: '일부 완료 · 확인 필요', failed: '실패' };
const REMOTE_LABELS = { 'not-configured': '미설정', pending: '업로드 대기', success: '업로드 완료', error: '업로드 실패 · 이 서버에 보존' };
const INTEGRITY_LABELS = { ok: '정상', failed: '실패', 'not-checked': '미검사' };
const BASE = `${API_BASE}/api/bo/backups`;
// 날짜 그룹 펼침 상태 — 개인 편의라 이 브라우저에만 기억한다. 저장소가 막혀 있으면 기본값(오늘·어제)으로 돈다.
const OPEN_DATES_KEY = 'bo.backup.openDates';
function readOpenDates(): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(OPEN_DATES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? new Set(parsed.filter((value): value is string => typeof value === 'string')) : null;
  } catch { return null; }
}
function writeOpenDates(dates: ReadonlySet<string>): void {
  try { window.localStorage.setItem(OPEN_DATES_KEY, JSON.stringify([...dates])); } catch { /* 기억 실패는 무시 — 화면은 그대로 동작한다 */ }
}

async function responseError(response: Response): Promise<Error> {
  if (response.status === 409) return new Error('다른 백업 또는 검증이 진행 중입니다. 최신 상태를 확인하세요.');
  if (response.status === 401 || response.status === 403) return new Error('BO 백업 관리 권한을 확인해 주세요.');
  const body = await response.json().catch(() => null) as { error?: string } | null;
  return new Error(body?.error || `백업 요청 실패 (HTTP ${response.status})`);
}

export function BackupStatusPanel(): JSX.Element {
  const [data, setData] = useState<BackupOverview | null>(null);
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<BackupAction | null>(null);
  const mounted = useRef(false);
  const posting = useRef(false);
  const timer = useRef<number | null>(null);
  const readController = useRef<AbortController | null>(null);
  const writeController = useRef<AbortController | null>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const confirmationTitle = useId();
  // null = 아직 손대지 않음 → 기본값(오늘·어제 펼침). 한 번 누르면 이 브라우저에 기억.
  const [openDates, setOpenDates] = useState<Set<string> | null>(readOpenDates);

  const refresh = useCallback(async (): Promise<void> => {
    if (!mounted.current || posting.current) return;
    if (timer.current !== null) window.clearTimeout(timer.current);
    readController.current?.abort();
    const controller = new AbortController(); readController.current = controller;
    setFetching(true);
    let delay = 5000;
    try {
      const response = await fetch(BASE, { credentials: 'include', signal: controller.signal });
      if (!response.ok) throw await responseError(response);
      const next = await response.json() as BackupOverview;
      if (controller.signal.aborted || !mounted.current) return;
      setData(next); setLoadError(null);
      delay = next.busy || next.lastJob?.status === 'running' ? 2000
        : next.coordinatorError === 'Backup status lookup failed.' ? 5000 : 30000;
    } catch (reason) {
      if (!controller.signal.aborted && mounted.current) setLoadError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (!controller.signal.aborted && mounted.current) {
        setFetching(false);
        timer.current = window.setTimeout(() => { void refresh(); }, delay);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true; void refresh();
    return () => {
      mounted.current = false; readController.current?.abort(); writeController.current?.abort();
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [refresh]);
  useEffect(() => { if (confirmation) confirmationRef.current?.focus(); }, [confirmation]);

  const running = !!data?.busy || data?.lastJob?.status === 'running';
  const statusUnavailable = data?.coordinatorError === 'Backup status lookup failed.';
  const reportSaveFailed = data?.coordinatorError === 'Verification report could not be saved.';
  const blocked = !data || fetching || submitting || running || !!loadError || statusUnavailable;
  const selectedSet = confirmation?.kind === 'verify' || confirmation?.kind === 'restore' ? data?.sets.find((set) => set.id === confirmation.setId) : null;
  const missingSet = (confirmation?.kind === 'verify' || confirmation?.kind === 'restore') && !selectedSet;
  const plan = confirmation?.kind === 'restore' && selectedSet ? restorePlan(selectedSet) : null;
  const job = data?.lastJob;
  const verification = (job?.kind === 'verify' ? job.verification : null) ?? data?.latestVerification;
  const configuration = data?.configuration;
  const localOnly = !!configuration && (!configuration.bucket || !configuration.prefix || !!configuration.error);
  const groups = groupBackupSetsByDate(data?.sets ?? []);
  const openSet = openDates ?? defaultOpenDates(groups);
  const toggleDate = (date: string, isOpen: boolean): void => {
    setOpenDates((current) => {
      const next = new Set(current ?? defaultOpenDates(groups));
      if (isOpen) next.add(date); else next.delete(date);
      // 목록에서 사라진 날짜는 같이 정리 — 기억이 무한히 늘지 않게.
      for (const stored of next) if (!groups.some((group) => group.date === stored)) next.delete(stored);
      writeOpenDates(next);
      return next;
    });
  };

  async function execute(): Promise<void> {
    if (!confirmation || blocked || posting.current || missingSet) return;
    const action = confirmation;
    posting.current = true; setSubmitting(true); setActionError(null); setNotice(null);
    if (timer.current !== null) window.clearTimeout(timer.current);
    readController.current?.abort();
    const controller = new AbortController(); writeController.current = controller;
    try {
      // '이 시점으로 복구'의 화면 동작은 격리 검증까지다. 운영 교체는 서버에서 bricklit-restore 로만 한다.
      const verifying = action.kind === 'verify' || action.kind === 'restore';
      const response = await fetch(verifying ? `${BASE}/${encodeURIComponent(action.setId)}/verify` : `${BASE}/run`, {
        method: 'POST', credentials: 'include', signal: controller.signal,
        ...(verifying ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind: action.kind }) }),
      });
      if (!response.ok) throw await responseError(response);
      const accepted = await response.json() as BackupJob;
      if (controller.signal.aborted || !mounted.current) return;
      setData((current) => current ? { ...current, busy: accepted.status === 'running', lastJob: accepted } : current);
      setConfirmation(null);
      setNotice(`${KIND_LABELS[verifying ? 'verify' : action.kind]} 요청이 접수되었습니다. 실행 결과는 아래 작업 상태에서 확인하세요.`);
    } catch (reason) {
      if (!controller.signal.aborted && mounted.current) setActionError(reason instanceof Error ? reason.message : '실행 결과를 확인하지 못했습니다. 상태 조회 후 다시 확인하세요.');
    } finally {
      posting.current = false;
      if (mounted.current) { setSubmitting(false); void refresh(); }
    }
  }

  return <BoPage title="백업·격리 복구 검증" subtitle="백업과 클라우드 업로드 상태를 구분해 확인합니다. 백업·검증은 한 번에 하나씩 실행됩니다."
    actions={<Button disabled={fetching || submitting} onClick={() => void refresh()}>백업 상태 새로고침</Button>}>
    {loadError && <p role="alert" className="rounded border border-red/40 bg-red/10 p-3 text-[14px] text-red">상태 조회 실패: {loadError} 5초 후 다시 조회합니다.</p>}
    {actionError && <p role="alert" className="rounded border border-red/40 bg-red/10 p-3 text-[14px] text-red">{actionError}</p>}
    {data?.coordinatorError && <p role="alert" className="rounded border border-red/40 bg-red/10 p-3 text-[14px] text-red">
      백업 운영 오류: {data.coordinatorError}{statusUnavailable && ' 상태를 확인할 때까지 수동 백업과 검증을 제한합니다.'}
    </p>}
    {notice && <p role="status" className="rounded border border-line bg-panel-2 p-3 text-[14px]">{notice}</p>}
    {!data && fetching && !loadError && <ContentSkeleton label="백업 상태를 불러오는 중…" variant="split" />}
    {data && <>
      <Section title="저장 위치·자동 백업">
        <div className="mb-4 rounded border border-line bg-panel-2 p-3 text-[14px]">
          {localOnly ? <><strong>백업은 이 서버에만 저장됩니다</strong><p className="mt-1 text-text-dim">{!configuration?.bucket ? 'S3 계정·버킷이 정해지지 않았습니다. 클라우드 업로드는 미설정 상태입니다.' : 'S3 설정을 완료해야 클라우드 업로드를 사용할 수 있습니다.'}</p></>
            : <><strong>백업은 이 서버에 저장되고 S3로 업로드됩니다</strong><p className="mt-1 text-text-dim">연결·업로드 성공 여부는 각 백업의 클라우드 업로드 상태에서 확인하세요.</p></>}
        </div>
        {configuration?.error && <p role="alert" className="mb-3 text-[14px] text-red">설정 오류: {configuration.error}</p>}
        <dl className="grid gap-3 text-[14px] sm:grid-cols-2">
          {([
            ['백업 저장 경로', configuration?.directory ?? '확인 필요'],
            ['자동 백업', data.automatic ? '사용' : '중지'],
            ['백업 주기', `DB ${data.intervals.db / 3600000}시간 · 미디어 ${data.intervals.media / 3600000}시간`],
            ['S3 버킷', configuration?.bucket ?? '미정'], ['S3 접두 경로', configuration?.prefix ?? '미정'],
            ['S3 리전 설정', configuration?.region ?? '확인 필요'],
          ]).map(([label, value]) => <div key={label}><dt className="text-text-dim">{label}</dt><dd className="mt-1 break-all font-medium text-text">{value}</dd></div>)}
        </dl>
      </Section>

      <Section title="수동 실행·작업 상태">
        <div className="mb-3 flex flex-wrap gap-2">
          <Button disabled={blocked || !!confirmation} onClick={() => setConfirmation({ kind: 'db' })}>DB 백업 실행</Button>
          <Button disabled={blocked || !!confirmation} onClick={() => setConfirmation({ kind: 'media' })}>미디어 백업 실행</Button>
        </div>
        <p role="status" className="text-[14px] text-text-dim">{submitting ? '요청을 보내는 중…' : running ? '백업 또는 검증 진행 중 · 2초 간격으로 상태를 조회합니다.' : statusUnavailable ? '상태 확인 불가 · 5초 간격으로 다시 조회합니다.' : '실행 대기 · 상태는 30초 간격으로 갱신합니다.'}</p>
        {job ? <div className="mt-3 space-y-1 rounded border border-line bg-panel-2 p-3 text-[14px]">
          <p className="font-semibold text-text">최근 작업: {KIND_LABELS[job.kind]} · <span className={job.status === 'failed' ? 'text-red' : job.status === 'partial' ? 'text-warning' : ''}>{JOB_LABELS[job.status]}</span></p>
          <p className="text-text-dim">시작 {formatDateTime(job.startedAt)}{job.finishedAt ? ` · 종료 ${formatDateTime(job.finishedAt)}` : ''}</p>
          {job.setId && <p className="break-all text-[12px] text-text-dim">백업 ID: {job.setId}</p>}
          {job.error && <p role="alert" className="text-red">{job.error}</p>}
          {job.status === 'partial' && <p className="text-warning">{job.kind === 'verify' ? '검증 보고서 저장 결과를 확인하세요.' : '원격 업로드 또는 보존 처리 결과를 확인하세요.'}</p>}
        </div> : <p className="mt-3 text-[13px] text-text-dim">현재 서버 실행 이후 작업 기록이 없습니다.</p>}
        {data.latestError && <div className="mt-3 rounded border border-red/40 bg-red/10 p-3 text-[13px]">
          <p className="font-semibold text-red">최근 서버 오류{data.latestError.kind ? ` · ${KIND_LABELS[data.latestError.kind]}` : ''} · {formatDateTime(data.latestError.createdAt)}</p>
          <p className="mt-1 text-text">{describeBackupError(data.latestError.code).title}</p>
          <p className="mt-1 text-text-dim">{describeBackupError(data.latestError.code).action} 같은 종류의 백업이 성공하면 이 안내는 사라집니다.</p>
          <p className="mt-1 break-all text-[12px] text-text-dim">{data.latestError.code}{data.latestError.setId ? ` · 백업 ${data.latestError.setId}` : ''}</p>
        </div>}
      </Section>

      {confirmation && <div ref={confirmationRef} role="group" aria-labelledby={confirmationTitle} tabIndex={-1}
        className="rounded border border-accent bg-panel-2 p-5 text-[14px] text-text focus:outline-none" aria-busy={submitting}>
        <h2 id={confirmationTitle} className="mb-2 text-[16px] font-semibold">{confirmation.kind === 'restore' ? '이 시점으로 복구' : `${KIND_LABELS[confirmation.kind]} 실행 확인`}</h2>
        <p>{confirmation.kind === 'restore' ? '운영 데이터 교체는 이 화면에서 실행되지 않습니다. 서버 셸에서 아래 명령을 치면 격리 복구·검증 후 교체하고, 실패하면 자동으로 되돌립니다. 여기서는 먼저 격리 검증을 돌려 이 세대가 온전한지 확인합니다.'
          : confirmation.kind === 'verify' ? '선택한 백업을 격리된 임시 디렉터리에 복원해 파일 해시·크기와 SQLite 무결성을 검사합니다. 운영 데이터는 유지됩니다.'
          : confirmation.kind === 'db' ? '온라인 DB 스냅샷을 백업 경로에 저장합니다.' : 'DB 스냅샷과 참조 미디어·썸네일을 백업합니다. 파일 크기에 따라 시간이 걸릴 수 있습니다.'}</p>
        {selectedSet && <p className="mt-2 break-all text-text-dim">{KIND_LABELS[selectedSet.kind]} · {formatDateTime(selectedSet.createdAt)} · {formatBytes(selectedSet.totalBytes)}<br />{selectedSet.id}</p>}
        {plan && <div className="mt-3 space-y-2 text-[13px]">
          {plan.notes.map((note) => <p key={note} className="text-text-dim">{note}</p>)}
          <dl className="space-y-1">
            <dt className="text-text-dim">① 서버에서 검증만 (운영 무변경)</dt><dd><code className="block select-all break-all rounded bg-panel px-2 py-1">{plan.dryRun}</code></dd>
            <dt className="text-text-dim">② 검증 통과 후 교체</dt><dd><code className="block select-all break-all rounded bg-panel px-2 py-1">{plan.apply}</code></dd>
            {plan.fromS3 && <><dt className="text-text-dim">이 서버 백업이 사라진 경우 (S3 세대)</dt><dd><code className="block select-all break-all rounded bg-panel px-2 py-1">{plan.fromS3}</code></dd></>}
          </dl>
          <p className="text-text-dim">서버 접속·절차: deploy/BACKUP_SECURITY_RUNBOOK.md §6 "운영 교체 복구".</p>
        </div>}
        {missingSet && <p role="alert" className="mt-2 text-red">선택한 백업이 더 이상 목록에 없습니다. 취소 후 다시 선택하세요.</p>}
        {(confirmation.kind === 'db' || confirmation.kind === 'media') && <p className="mt-2 text-text-dim">{localOnly ? '이번 실행은 이 서버에만 저장됩니다. (클라우드 업로드 미설정)' : '백업 완료 후 클라우드 업로드도 함께 시도합니다.'}</p>}
        <div className="mt-4 flex gap-2"><Button disabled={submitting} onClick={() => setConfirmation(null)}>취소</Button>
          <Button variant="primary" disabled={blocked || missingSet} onClick={() => void execute()}>{submitting ? '요청 중…' : confirmation.kind === 'restore' ? '격리 검증 먼저 실행' : '확인 후 실행'}</Button></div>
      </div>}

      <Section title="완료된 백업">
        {groups.length === 0
          ? <p className="py-6 text-center text-[14px] text-text-dim">{statusUnavailable ? '백업 목록을 확인할 수 없습니다. 상태를 다시 조회하고 있습니다.' : '완료된 백업이 없습니다.'}</p>
          : <>
            <p className="mb-3 text-[13px] text-text-dim">날짜별로 묶었습니다. 오늘·어제는 펼쳐져 있고, 나머지는 날짜를 누르면 펼쳐집니다. 펼침 상태는 이 브라우저에 기억됩니다.</p>
            <div className="space-y-2">
              {groups.map((group) => {
                const open = openSet.has(group.date);
                return <details key={group.date} open={open} className="rounded border border-line bg-panel-2"
                  onToggle={(event) => { const next = (event.currentTarget as HTMLDetailsElement).open; if (next !== open) toggleDate(group.date, next); }}>
                  <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 text-[14px] [&::-webkit-details-marker]:hidden">
                    <span aria-hidden="true" className="text-text-dim">{open ? '▾' : '▸'}</span>
                    <span className="font-semibold text-text">{group.label}</span>
                    <span className={group.counts.failed ? 'text-red' : 'text-text-dim'}>{dateGroupSummary(group, !localOnly)}</span>
                  </summary>
                  <div className="overflow-x-auto border-t border-line">
                    <table className="w-full text-left text-[13px] text-text">
                      <caption className="sr-only">{group.label} 백업 세트별 완료·클라우드 업로드 상태</caption>
                      <thead className="border-b border-line text-text-dim"><tr>{['종류·생성 시각', '파일·용량', '클라우드 업로드', '검증·복구'].map((label) => <th key={label} scope="col" className="px-2 py-2 font-medium">{label}</th>)}</tr></thead>
                      <tbody>{group.sets.map((set) => <tr key={set.id} className="border-b border-line last:border-0">
                        <td className="px-2 py-3"><p className="font-semibold">{KIND_LABELS[set.kind]}</p><p className="mt-1 text-text-dim">{formatDateTime(set.createdAt)}</p><p className="mt-1 max-w-[300px] break-all text-[11px] text-text-dim">{set.id}</p></td>
                        <td className="px-2 py-3">{set.fileCount}개 · {formatBytes(set.totalBytes)}</td>
                        <td className="px-2 py-3"><span className={set.remote.status === 'error' ? 'text-red' : set.remote.status === 'success' ? 'text-green' : 'text-text-dim'}>{REMOTE_LABELS[set.remote.status]}</span>
                          {set.remote.checkedAt && <p className="mt-1 text-[12px] text-text-dim">{formatDateTime(set.remote.checkedAt)}</p>}
                          {set.remote.error && <p className="mt-1 max-w-[260px] text-[12px] text-red">{set.remote.error.message}</p>}</td>
                        <td className="px-2 py-3"><div className="flex flex-wrap gap-1">
                          <Button disabled={blocked || !!confirmation} aria-label={`${KIND_LABELS[set.kind]} ${formatDateTime(set.createdAt)} 격리 복구 검증`}
                            onClick={() => setConfirmation({ kind: 'verify', setId: set.id })}>격리 복구 검증</Button>
                          <Button disabled={blocked || !!confirmation} aria-label={`${KIND_LABELS[set.kind]} ${formatDateTime(set.createdAt)} 시점으로 복구 안내`}
                            onClick={() => setConfirmation({ kind: 'restore', setId: set.id })}>이 시점으로 복구</Button>
                        </div></td>
                      </tr>)}</tbody>
                    </table>
                  </div>
                </details>;
              })}
            </div>
          </>}
      </Section>

      <Section title={reportSaveFailed ? '격리 검증 결과 · 보고서 저장 실패' : '저장된 격리 검증 결과'}>
        {verification ? <div className="space-y-2 text-[14px]">
          <p className={`font-semibold ${verification.ok ? 'text-green' : 'text-red'}`}>{verification.ok ? '격리 복구 검증 통과' : '격리 복구 검증 실패'}</p>
          <p className="break-all text-[12px] text-text-dim">검증 대상 백업: {verification.id}</p>
          <p className="text-text">파일 {verification.files} / {verification.expectedFiles}개 · {formatBytes(verification.bytes)} · SQLite 무결성 {INTEGRITY_LABELS[verification.integrity]}</p>
          {verification.errors.map((report, index) => <p key={`${report.code}:${index}`} role="alert" className="text-red">{report.code}: {report.message}</p>)}
          <p className="text-[13px] text-text-dim">백업 세트를 격리된 임시 경로에서 복원·검사한 결과입니다.</p>
        </div> : <p className="text-[14px] text-text-dim">{data.coordinatorError === 'Verification report could not be read.' ? '검증 보고서를 읽을 수 없습니다.' : '저장된 격리 복구 검증 결과가 없습니다.'}</p>}
      </Section>
    </>}
  </BoPage>;
}
